import { GridCellKind } from '@glideapps/glide-data-grid';
import type { EditableGridCell, GridCell } from '@glideapps/glide-data-grid';
import type { FundColumnEdit, FundListItemView, FundSummary, ResearchStatus } from '@nemo/api';
import {
  FUND_CSV_COLUMNS,
  STAGE_LABELS,
  formatCheque,
  chequeDisplay,
  saidPhrase,
  stageChoicesOf,
  type FundStageView,
  type Stage,
  type StageConflict,
  type StageSourceFact,
  throughWords,
} from '@nemo/core/model';
import {
  chipsCell,
  linesCell,
  multiPicklistCell,
  singlePicklistCell,
  stagesCell,
  type Chip,
} from './cells/index.js';
import {
  INVESTOR_TYPE_OPTIONS,
  formatRoundLabel,
  formatRoundMonth,
  investorTypeLabel,
  contactKindSuffix,
  lastContactText,
} from '../dealroom/format.js';
import { gridTheme } from '../palette.js';
import type { ListItemEdit } from '../lists/useFundList.js';
import { formatDay } from '../ui/format.js';
import { GRID_ICON_TEXT_PADDING } from '../ui/SiteIcon.js';
import type { GridColumnDef } from './columns.js';
import { type FundLists, type FundWrite, idsForLabels } from './fundWrites.js';
import {
  type Parsed,
  parseChequeText,
  parseCountriesText,
  parseListText,
  parseStageChoicesText,
  parseTextValue,
  parseTierText,
} from './paste.js';

/**
 * Everything a cell needs that is not on the row: the picklist vocabularies,
 * and where each fund's research has got to.
 */
export interface FundGridContext extends FundLists {
  readonly research: ReadonlyMap<string, ResearchStatus>;
  /** Whether the rows are tall enough for a cell to use a second line. */
  readonly twoLine: boolean;
  /** In a list view, each fund's item on the list, by fund id. */
  readonly listItems?: ReadonlyMap<string, FundListItemView> | undefined;
}

export interface FundColumnDef extends GridColumnDef<FundSummary, FundGridContext> {
  /** The write that puts the column back as it is now, which is what undo needs. */
  readonly current?: (fund: FundSummary, lists: FundLists) => FundWrite;
  /** The write a finished in-grid edit asks for. */
  readonly commit?: (fund: FundSummary, cell: EditableGridCell) => FundWrite | null;
  /** The write a pasted string asks for. Absent where a paste makes no sense. */
  readonly paste?: (fund: FundSummary, text: string) => Parsed<FundWrite>;
  /** In a list view: the list column this grid column shows. */
  readonly listKey?: string;
  /**
   * What a CSV or a sheet writes for this column, where that is not the one
   * column the grid shows: Stages is one cell here and two columns there.
   * Each key is a list column key, which is what the Sheets export reads.
   */
  readonly exports?: readonly {
    readonly key: string;
    readonly title: string;
    readonly text: (fund: FundSummary) => string;
  }[];
  /**
   * In a list view, a column about the item rather than the fund: what a
   * finished edit asks the list to change. Such a column has no `commit`.
   */
  readonly itemEdit?: (cell: EditableGridCell) => ListItemEdit | null;
}

/** The columns a CSV or a sheet writes for these grid columns, in order. */
export function exportColumnsOf(
  columns: readonly FundColumnDef[],
  context: FundGridContext,
): { key: string; title: string; text: (fund: FundSummary) => string }[] {
  return columns.flatMap((column) =>
    column.exports === undefined
      ? [{ key: column.id, title: column.title, text: (fund) => column.text(fund, context) }]
      : column.exports.map((entry) => ({ ...entry })),
  );
}

export function text(
  value: string,
  editable: boolean,
  wrap = false,
  /** What the cell shows where that is not what editing starts from. */
  shown = value,
): GridCell {
  return {
    kind: GridCellKind.Text,
    data: value,
    displayData: shown,
    allowOverlay: editable,
    readonly: !editable,
    allowWrapping: wrap,
  };
}

export function readText(cell: EditableGridCell): string | null {
  return cell.kind === GridCellKind.Text ? cell.data : null;
}

function readUri(cell: EditableGridCell): string | null {
  return cell.kind === GridCellKind.Uri ? cell.data : null;
}

function readNumber(cell: EditableGridCell): number | undefined | null {
  return cell.kind === GridCellKind.Number ? cell.data : null;
}

export function readSelection(
  cell: EditableGridCell,
  kind: 'multiPicklist',
): readonly string[] | null;
export function readSelection(cell: EditableGridCell, kind: 'singlePicklist'): string | null;
export function readSelection(
  cell: EditableGridCell,
  kind: 'multiPicklist' | 'singlePicklist',
): readonly string[] | string | null {
  if (cell.kind !== GridCellKind.Custom) return null;
  if (!('kind' in cell.data) || cell.data.kind !== kind) return null;
  if (kind === 'multiPicklist') {
    return 'selected' in cell.data && Array.isArray(cell.data.selected) ? cell.data.selected : null;
  }
  return 'value' in cell.data && typeof cell.data.value === 'string' ? cell.data.value : null;
}

function one(edit: FundColumnEdit): FundWrite {
  return { kind: 'edits', edits: [edit] };
}

function mapped<T>(parsed: Parsed<T>, into: (value: T) => FundWrite): Parsed<FundWrite> {
  return parsed.ok ? { ok: true, value: into(parsed.value) } : parsed;
}

/** How many partners are known at this fund beyond the pairing being shown. */
export function otherPartnerCount(fund: FundSummary): number {
  return new Set(fund.whoKnows.map((edge) => edge.investorName)).size - 1;
}

/** The flag on a fund whose only decent relationships belong to people who left, or to support staff. */
export const ONLY_FORMER_TEAM = 'Only former or support staff know them';

/** A fund nobody knows still has people on file, or has nobody at all. */
function partnersNote(partnerCount: number): string {
  if (partnerCount === 0) return 'no partners';
  return partnerCount === 1 ? '1 partner' : `${partnerCount} partners`;
}

function whoKnowsPair(edge: FundSummary['whoKnows'][number]): string {
  const pair = `${edge.investorName} · ${edge.memberName}`;
  return edge.through === null ? pair : `${pair} (${throughWords(edge.through)})`;
}

/**
 * Every pairing, strongest first — what the compact cell is a summary of, with
 * the last contact where Affinity knows one, because "who knows them best" and
 * "who spoke to them last" are not the same question.
 */
export function whoKnowsPairs(fund: FundSummary): string[] {
  return fund.whoKnows.map((edge) => {
    const pair = whoKnowsPair(edge);
    const day = formatDay(edge.lastContactAt);
    return day === null ? pair : `${pair} · last contact ${day}`;
  });
}

/** "Seed (leads), Series A": what a Stages cell copies, and what a paste reads back. */
export function stagesText(fund: FundSummary): string {
  return fund.stages
    .filter((entry) => entry.participates)
    .map((entry) => `${STAGE_LABELS[entry.stage]}${entry.leads ? ' (leads)' : ''}`)
    .join(', ');
}

function rounds(count: number): string {
  return `${count} round${count === 1 ? '' : 's'}`;
}

/** "leads", "invests, does not lead", "does not invest": one source's say. */
function saysWords(fact: StageSourceFact): string {
  const parts: string[] = [];
  if (fact.leads === 'asserts') parts.push('leads');
  else if (fact.invests === 'asserts') parts.push('invests');
  if (fact.invests === 'denies') parts.push('does not invest');
  else if (fact.leads === 'denies') parts.push('does not lead');
  return parts.join(', ');
}

/** What one source says, in a few words: "sheet says invests". */
function sourceSays(fact: StageSourceFact, entry: FundStageView): string {
  switch (fact.source) {
    case 'person':
      return `a person said: ${saysWords(fact)}`;
    case 'sheet':
      return `sheet says ${saysWords(fact)}`;
    case 'rounds': {
      const seen = entry.observed;
      if (seen === null) return 'seen in rounds';
      return seen.led > 0
        ? `observed: led ${rounds(seen.led)} of ${seen.rounds}`
        : `observed: on ${rounds(seen.rounds)}, led none`;
    }
    case 'dealroom':
    case 'classifier':
    case 'linkedin':
      return `${fact.source} says ${saysWords(fact)}`;
  }
}

/**
 * "You said it does not lead at Series A; since then: led Arlequin AI Series A
 * (Mar 2026)": a person's answer newer evidence disagrees with.
 */
export function conflictText(conflict: StageConflict, stage: Stage): string {
  const said = `You said it ${saidPhrase(conflict, STAGE_LABELS[stage])}`;
  return conflict.evidence.length === 0
    ? `${said}; newer evidence disagrees`
    : `${said}; since then: ${conflict.evidence.join(', ')}`;
}

/**
 * One stage in a line: "Leads at Series A (observed: led 3 rounds of 4) ·
 * sheet says invests". The observed evidence sits beside the answer, every
 * other source's say after it, and a conflict last. The grid's hover is one of
 * these per stage.
 */
export function stageTitle(entry: FundStageView): string {
  const label = STAGE_LABELS[entry.stage];
  const answer = entry.leads
    ? `Leads at ${label}`
    : entry.participates
      ? `Invests at ${label}`
      : `Not at ${label}`;
  const observed = entry.sources.find((fact) => fact.source === 'rounds');
  const head = observed === undefined ? answer : `${answer} (${sourceSays(observed, entry)})`;
  const rest = entry.sources
    .filter((fact) => fact.source !== 'rounds')
    .map((fact) => sourceSays(fact, entry));
  const conflicts = entry.conflicts.map((conflict) => `⚠ ${conflictText(conflict, entry.stage)}`);
  return [[head, ...rest].join(' · '), ...conflicts].join('\n');
}

/** Sector tags the recent rounds hit that the fund's own list does not carry. */
export function unclaimedSectors(fund: FundSummary): string[] {
  const claimed = new Set(fund.sectors.map((sector) => sector.toLowerCase()));
  return fund.derivedSectors.filter((tag) => !claimed.has(tag.toLowerCase()));
}

/** "DACH 40% (8 deals)", one region of the fund's recent rounds. */
function regionShareText(entry: FundSummary['observedRegions'][number]): string {
  return `${entry.region} ${Math.round(entry.share * 100)}% (${entry.deals} deal${entry.deals === 1 ? '' : 's'})`;
}

/** The hover on a Regions cell and the pane's Active in cell: every region with its share. */
export function regionsTitle(fund: FundSummary): string | null {
  return fund.observedRegions.length === 0
    ? null
    : `Recent rounds by region: ${fund.observedRegions.map(regionShareText).join(' · ')}`;
}

function observedChips(labels: readonly string[]): Chip[] {
  return labels.map((label) => ({ label, tone: 'neutral' as const, style: 'hollow' as const }));
}

/** "Arlequin AI · Series A · Sep 2026", one round to a line. */
function recentInvestmentLines(fund: FundSummary): string[] {
  return fund.recentInvestments.map((round) => {
    const parts = [
      round.companyName,
      formatRoundLabel(round.roundLabel),
      formatRoundMonth(round.month),
    ];
    return parts.filter((part) => part !== '').join(' · ');
  });
}

/**
 * The same lines with who at the fund did each round, for the hover: the cell
 * has room for what happened, the hover for who to ask about it.
 */
export function recentInvestmentTipLines(fund: FundSummary): string[] {
  const lines = recentInvestmentLines(fund);
  return fund.recentInvestments.map((round, index) => {
    const line = lines[index] ?? round.companyName;
    return round.partnerName === null ? line : `${line} (${round.partnerName})`;
  });
}

/** How many portfolio company names a Co-invested cell shows before counting. */
const CO_INVESTMENTS_SHOWN = 3;

/**
 * "Kry, Voi, Vay +2": the names, then how many more. The count and each
 * company's status are the hover.
 */
function coInvestedLines(fund: FundSummary): string[] {
  const count = fund.coInvestmentCount;
  if (count === 0) return [];
  const names = recorded(fund)
    .slice(0, CO_INVESTMENTS_SHOWN)
    .map((row) => row.company);
  const rest = count - names.length;
  return [`${names.join(', ')}${rest > 0 ? ` +${rest}` : ''}`];
}

/**
 * The companies the fund accounting records. The ones only Dealroom saw are
 * evidence rather than the record, and the cell counts the record.
 */
function recorded(fund: FundSummary): FundSummary['coInvestments'] {
  return fund.coInvestments.filter((row) => !row.observedOnly);
}

/**
 * The hover: how many companies the record holds, then "Kry · Value Driver ·
 * PAV II, OPP II", one company to a line, then the ones only Dealroom saw,
 * said as such.
 */
export function coInvestmentLines(fund: FundSummary): string[] {
  const count = fund.coInvestmentCount;
  const heading = count === 0 ? [] : [`${count} portfolio compan${count === 1 ? 'y' : 'ies'}`];
  const lines = fund.coInvestments.map((row) =>
    [
      row.company,
      row.status,
      row.projectAFunds.join(', '),
      row.observedOnly ? 'seen on Dealroom only' : '',
    ]
      .filter((part) => part !== '')
      .join(' · '),
  );
  return [...heading, ...lines];
}

export const FUND_COLUMNS: readonly FundColumnDef[] = [
  {
    id: 'name',
    title: FUND_CSV_COLUMNS.name.title,
    width: 220,
    // The name's own editor is what Enter is for on the name; from anywhere
    // else on the row it opens the fund.
    editsOnEnter: true,
    // A Uri cell for the styling rather than for a link: the name reads as the
    // way into the fund, and opening the fund is what clicking it does — the
    // grid answers that click itself. `onClickUri` exists only because its
    // presence is what puts the overlay into edit mode rather than into link
    // preview, so Enter and a double click still type a new name; it must not
    // prevent the default, or the click that opens the editor never lands.
    cell: (fund) => ({
      kind: GridCellKind.Uri,
      data: fund.name,
      displayData: fund.name,
      allowOverlay: true,
      hoverEffect: true,
      onClickUri: () => undefined,
      // Room for the site icon FundsGrid paints into the cell's left edge.
      themeOverride: { cellHorizontalPadding: GRID_ICON_TEXT_PADDING },
    }),
    text: FUND_CSV_COLUMNS.name.text,
    current: (fund) => one({ column: 'name', value: fund.name }),
    commit: (_fund, cell) => {
      const value = readUri(cell)?.trim();
      return value === undefined || value === '' ? null : one({ column: 'name', value });
    },
    paste: (_fund, value) =>
      value.trim() === ''
        ? { ok: false, reason: 'A fund needs a name' }
        : { ok: true, value: one({ column: 'name', value: value.trim() }) },
  },
  {
    id: 'whoKnows',
    title: FUND_CSV_COLUMNS.whoKnows.title,
    width: 320,
    // The strongest pairing and a count of the rest. Warmth is the colour
    // because warmth is the judgement; a pair Affinity also has a signal for
    // gets a ring, so the machine fact reads as an addition to the judgement
    // rather than a competing one. The full list is one click away.
    cell: (fund) => {
      const strongest = fund.whoKnows[0];
      // Nobody here, but somebody who left knew them well: said, faintly, so
      // the fund is not mistaken for one nobody ever had a way into.
      if (strongest === undefined && fund.onlyFormerTeam) {
        return chipsCell([], { note: ONLY_FORMER_TEAM });
      }
      if (strongest === undefined) return chipsCell([], { note: partnersNote(fund.partnerCount) });
      const others = otherPartnerCount(fund);
      return chipsCell(
        [
          {
            label: whoKnowsPair(strongest),
            tone: strongest.warmth,
            style: strongest.affinityStrength === null ? undefined : 'ringed',
          },
        ],
        { note: others > 0 ? `+${others} other${others === 1 ? '' : 's'}` : undefined },
      );
    },
    text: FUND_CSV_COLUMNS.whoKnows.text,
  },
  {
    id: 'tier',
    title: FUND_CSV_COLUMNS.tier.title,
    width: 70,
    cell: (fund) => ({
      kind: GridCellKind.Number,
      data: fund.tier ?? undefined,
      displayData: fund.tier === null ? '' : String(fund.tier),
      allowOverlay: true,
      allowNegative: false,
    }),
    text: FUND_CSV_COLUMNS.tier.text,
    current: (fund) => one({ column: 'tier', value: fund.tier }),
    commit: (_fund, cell) => {
      const value = readNumber(cell);
      if (value === null) return null;
      if (value === undefined) return one({ column: 'tier', value: null });
      return Number.isInteger(value) && value >= 0 && value <= 5
        ? one({ column: 'tier', value })
        : null;
    },
    paste: (_fund, value) =>
      mapped(parseTierText(value), (tier) => one({ column: 'tier', value: tier })),
  },
  {
    id: 'stages',
    title: 'Stages',
    width: 300,
    note: 'One chip per stage the fund invests at; a dot where it leads there. A hollow chip is a stage only its rounds of the last 24 months show. Merged from the spreadsheet, the rounds and anything a person set here, which decides its stage. The rounds count a stage at a fifth of all its rounds, and a lead at a fifth of its rounds there; the spreadsheet never says where it leads.',
    cell: (fund) => stagesCell(fund.stages, stagesText(fund)),
    text: stagesText,
    exports: [
      { key: 'stagesInvests', ...FUND_CSV_COLUMNS.stagesInvests },
      { key: 'stagesLeads', ...FUND_CSV_COLUMNS.stagesLeads },
    ],
    current: (fund) => ({ kind: 'stages', stages: stageChoicesOf(fund) }),
    commit: (_fund, cell) => {
      if (cell.kind !== GridCellKind.Custom) return null;
      if (!('kind' in cell.data) || cell.data.kind !== 'stages') return null;
      return 'choices' in cell.data && Array.isArray(cell.data.choices)
        ? { kind: 'stages', stages: cell.data.choices }
        : null;
    },
    paste: (_fund, value) =>
      mapped(parseStageChoicesText(value), (stages) => ({ kind: 'stages', stages })),
  },
  {
    id: 'investorType',
    title: 'Type',
    width: 120,
    // Read-only, unlike every other picklist here: Dealroom's match writes this
    // as part of the fund's identity, and no procedure takes it from a person.
    cell: (fund) =>
      singlePicklistCell(fund.investorType ?? '', INVESTOR_TYPE_OPTIONS, { editable: false }),
    text: (fund) => investorTypeLabel(fund.investorType),
  },
  {
    id: 'hqCountries',
    title: FUND_CSV_COLUMNS.hqCountries.title,
    width: 100,
    cell: (fund) => text(fund.hqCountries.join(', '), true),
    text: FUND_CSV_COLUMNS.hqCountries.text,
    current: (fund) => one({ column: 'hqCountries', value: fund.hqCountries }),
    commit: (_fund, cell) => {
      const value = readText(cell);
      if (value === null) return null;
      const parsed = parseCountriesText(value);
      return parsed.ok ? one({ column: 'hqCountries', value: parsed.value }) : null;
    },
    paste: (_fund, value) =>
      mapped(parseCountriesText(value), (codes) => one({ column: 'hqCountries', value: codes })),
  },
  {
    id: 'regions',
    title: FUND_CSV_COLUMNS.regions.title,
    width: 200,
    note: 'Where the companies in the last 24 months of rounds are based. Observed, not saved, so it cannot be edited.',
    cell: (fund) => ({
      ...multiPicklistCell(
        [],
        [],
        observedChips(fund.observedRegions.map((entry) => entry.region)),
      ),
      allowOverlay: false,
      copyData: FUND_CSV_COLUMNS.regions.text(fund),
    }),
    text: FUND_CSV_COLUMNS.regions.text,
  },
  {
    id: 'recentInvestments',
    title: 'Recent investments',
    width: 300,
    // What a fund has actually been doing, which is the question a count was
    // only ever standing in for. Clicking it opens the panel on those rounds.
    cell: (fund) => linesCell(recentInvestmentLines(fund), 'none in 24 months', fund.recentRounds),
    text: (fund) => recentInvestmentLines(fund).join('; '),
  },
  {
    id: 'lastContact',
    title: 'Last contact',
    width: 110,
    note: 'The team\'s latest email or meeting with anybody at the fund, per Affinity, or a contact somebody reported. Faint: more than two years ago. "Emailed, unchecked": only emails nobody has checked the direction of; "inbound only": checked, and nobody here wrote.',
    cell: (fund) => {
      const said = lastContactText(fund.lastContact);
      return {
        ...text(said.text, false),
        ...(said.dim ? { themeOverride: { textDark: gridTheme().textLight ?? '' } } : {}),
      };
    },
    // Whether a recent contact was genuine: faint, beside the date it qualifies.
    faintSuffix: (fund) => contactKindSuffix(fund.lastContact.contact),
    text: (fund) => lastContactText(fund.lastContact).text,
  },
  {
    id: 'coInvested',
    title: 'Co-invested',
    width: 240,
    note: "Portfolio companies where this fund invested alongside Project A, from Project A's fund accounting.",
    // Read-only here: the fund accounting is the record, changed by a
    // re-import or in the panel. Clicking it opens the panel, where the full
    // list is, with the rounds Dealroom saw.
    cell: (fund) => {
      const lines = coInvestedLines(fund);
      return linesCell(lines, '', lines.length);
    },
    text: (fund) =>
      recorded(fund)
        .map((row) => `${row.company} (${row.status})`)
        .join('; '),
  },
  {
    id: 'notableInvestments',
    title: FUND_CSV_COLUMNS.notableInvestments.title,
    width: 240,
    cell: (fund) => text(fund.notableInvestments.join(', '), true),
    text: FUND_CSV_COLUMNS.notableInvestments.text,
    current: (fund) => one({ column: 'notableInvestments', value: fund.notableInvestments }),
    commit: (_fund, cell) => {
      const value = readText(cell);
      if (value === null) return null;
      const parsed = parseListText(value);
      return parsed.ok ? one({ column: 'notableInvestments', value: parsed.value }) : null;
    },
    paste: (_fund, value) =>
      mapped(parseListText(value), (list) => one({ column: 'notableInvestments', value: list })),
  },
  {
    id: 'sectors',
    title: FUND_CSV_COLUMNS.sectors.title,
    width: 260,
    note: 'A hollow chip is a tag observed from recent rounds; it is not saved and cannot be edited.',
    cell: (fund, lists) =>
      multiPicklistCell(
        idsForLabels(fund.sectors, lists.sectors),
        lists.sectors,
        observedChips(unclaimedSectors(fund)),
      ),
    text: FUND_CSV_COLUMNS.sectors.text,
    current: (fund, lists) => ({
      kind: 'sectors',
      sectorIds: idsForLabels(fund.sectors, lists.sectors),
    }),
    commit: (_fund, cell) => {
      const selected = readSelection(cell, 'multiPicklist');
      return selected === null ? null : { kind: 'sectors', sectorIds: selected };
    },
  },
  {
    id: 'owners',
    title: FUND_CSV_COLUMNS.owners.title,
    width: 200,
    cell: (fund, lists) =>
      multiPicklistCell(idsForLabels(fund.owners, lists.members), lists.members),
    text: FUND_CSV_COLUMNS.owners.text,
    current: (fund, lists) => ({
      kind: 'owners',
      memberIds: idsForLabels(fund.owners, lists.members),
    }),
    commit: (_fund, cell) => {
      const selected = readSelection(cell, 'multiPicklist');
      return selected === null ? null : { kind: 'owners', memberIds: selected };
    },
  },
  {
    id: 'cheque',
    title: FUND_CSV_COLUMNS.cheque.title,
    width: 230,
    // What the cell shows is the euro range (and, painted after it, its
    // qualifier); `data` stays the stated cheque in its own currency, so
    // opening the editor or copying starts from what a person typed, and
    // an estimate leaves it blank.
    cell: (fund) => text(formatCheque(fund), true, false, chequeDisplay(fund)?.range ?? ''),
    faintSuffix: (fund) => chequeDisplay(fund)?.qualifier ?? '',
    // Search sees the whole reading, qualifier and estimate included.
    text: (fund) => chequeDisplay(fund)?.text ?? '',
    exports: [
      { key: 'cheque', ...FUND_CSV_COLUMNS.cheque },
      { key: 'chequeEstimate', ...FUND_CSV_COLUMNS.chequeEstimate },
    ],
    // One cell, three columns: a range has to move as a whole or not at all.
    current: (fund) => ({
      kind: 'edits',
      edits: [
        { column: 'chequeMin', value: fund.chequeMin },
        { column: 'chequeMax', value: fund.chequeMax },
        { column: 'chequeCurrency', value: fund.chequeCurrency },
      ],
    }),
    commit: (_fund, cell) => {
      const value = readText(cell);
      if (value === null) return null;
      const parsed = parseChequeText(value);
      return parsed.ok ? chequeWrite(parsed.value) : null;
    },
    paste: (_fund, value) => mapped(parseChequeText(value), chequeWrite),
  },
  {
    id: 'notes',
    title: FUND_CSV_COLUMNS.notes.title,
    width: 320,
    cell: (fund, context) => text(fund.notes ?? '', true, context.twoLine),
    text: FUND_CSV_COLUMNS.notes.text,
    current: (fund) => one({ column: 'notes', value: fund.notes }),
    commit: (_fund, cell) => {
      const value = readText(cell);
      return value === null ? null : one({ column: 'notes', value });
    },
    paste: (_fund, value) =>
      mapped(parseTextValue(value), (notes) => one({ column: 'notes', value: notes })),
  },
];

export function chequeWrite(range: {
  min: number | null;
  max: number | null;
  currency: string | null;
}): FundWrite {
  return {
    kind: 'edits',
    edits: [
      { column: 'chequeMin', value: range.min },
      { column: 'chequeMax', value: range.max },
      { column: 'chequeCurrency', value: range.currency },
    ],
  };
}
